// =============================================================================
// Meadowhall Daily Forecast — self-contained module (Phase 3 fold-in, 6 Jul).
//
// Ported VERBATIM from the standalone rolled-meadowhall-siteapp so it is byte-
// identical to the proven Sheffield forecast (its own backend, per-SKU editor,
// override slider, PIN-gated confirm/undo). Kept in its OWN module with its OWN
// styles + colours so folding Meadowhall into the shared app touches NOTHING in
// the FOH bundle's code path for Trafford/Arndale/kitchen — they never render it.
//
// Wired in foh.jsx ONLY for the meadowhall site: the forecast tile + view pass
// backend={SITE.forecastBackend} (the separate Sheffield script). The Manchester
// BACKEND (imported below) still serves forecaststatus / previousdaywaste / the
// forecastConfirm snapshot, exactly as in the standalone app.
// =============================================================================
import React from "react";
import { BACKEND } from "../config/sites.js";
const { useState, useEffect, useCallback, useMemo } = React;

const B = {nori:"#00392C",wasabi:"#D4FB6F",kewpie:"#FEFAF0",rice:"#FFFFFF",coral:"#FF7A5C",sunshine:"#F1C544",eucalyptus:"#6FAF8F"};

// STOPGAP (2026-09-06): Adam's Meadowhall forecast script still carries the retired
// summer specials in its roll list, so they surface here at 0% and clutter the
// kitchen's list (staff have to zero them each submission). Hide them at the display
// layer until Adam removes them from his Sheffield script's roll list — then delete
// this. Matched case-insensitively and trimmed against the short names his backend
// returns ("Honey Soy", "Fish & Chips", "LL Salmon").
const MH_RETIRED_ROLLS = new Set(["honey soy", "fish & chips", "ll salmon"]);
function stripRetiredRolls(data) {
  if (data && Array.isArray(data.skuBreakdown)) {
    data.skuBreakdown = data.skuBreakdown.filter(function(s) {
      return !MH_RETIRED_ROLLS.has(String((s && s.name) || "").trim().toLowerCase());
    });
  }
  return data;
}

const S = {
  // Login
  loginRoot:{minHeight:"100vh",background:B.nori,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif",position:"relative",animation:"viewIn 0.18s ease both"},
  strip:{position:"absolute",top:0,left:0,right:0,background:"rgba(0,0,0,0.2)",padding:"10px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8},
  stripDot:{width:8,height:8,borderRadius:"50%",background:B.wasabi},stripTxt:{fontWeight:700,fontSize:"0.8rem",color:B.kewpie,letterSpacing:"0.08em",textTransform:"uppercase"},
  loginBody:{display:"flex",flexDirection:"column",alignItems:"center",width:"100%",maxWidth:340},
  dots:{display:"flex",gap:20,marginBottom:10},
  dot:{width:20,height:20,borderRadius:"50%",border:"2.5px solid rgba(212,251,111,0.3)",borderColor:"rgba(212,251,111,0.3)",background:"transparent",boxShadow:"none",transition:"all 0.15s ease"},
  dotFilled:{borderColor:B.wasabi,background:B.wasabi,boxShadow:"0 0 14px rgba(212,251,111,0.35)"},
  dotEmpty:{borderColor:"rgba(212,251,111,0.3)",background:"transparent",boxShadow:"none"},
  pinErr:{color:B.coral,fontSize:"0.82rem",fontWeight:600,height:20,marginBottom:8},pinHint:{color:B.kewpie,opacity:0.45,fontSize:"0.82rem",height:20,marginBottom:8},
  pad:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,width:"100%",maxWidth:280},
  key:{aspectRatio:"1.35",borderRadius:14,border:"none",background:"rgba(212,251,111,0.1)",color:B.wasabi,fontSize:"1.6rem",fontFamily:"Manrope,sans-serif",fontWeight:700,cursor:"pointer",transition:"all 0.1s ease",display:"flex",alignItems:"center",justifyContent:"center"},
  loginFoot:{color:B.kewpie,opacity:0.25,fontSize:"0.75rem",textAlign:"center",paddingBottom:16},
  flash:{position:"fixed",top:0,left:0,right:0,bottom:0,width:"100%",height:"100%",background:B.nori,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:100},
  greet:{fontFamily:"gelica,serif",fontSize:"2.8rem",fontWeight:700,color:B.wasabi,animation:"greetIn 1s ease forwards",textAlign:"center",padding:"0 1rem",width:"100%",maxWidth:600},

  // Home
  homeRoot:{minHeight:"100vh",background:B.nori,fontFamily:"Manrope,sans-serif",padding:"0 2rem 1rem",display:"flex",flexDirection:"column",animation:"viewIn 0.2s ease both"},
  topBar:{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid rgba(212,251,111,0.1)"},
  topL:{display:"flex",alignItems:"center",gap:14},
  hSite:{background:"rgba(212,251,111,0.12)",border:"1px solid rgba(212,251,111,0.2)",color:B.wasabi,borderRadius:999,padding:"4px 14px",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.04em",textTransform:"uppercase"},
  topR:{display:"flex",alignItems:"center",gap:12},chip:{display:"flex",alignItems:"center",gap:8,background:"rgba(254,250,240,0.07)",borderRadius:999,padding:"4px 14px 4px 4px"},
  chipAv:{width:28,height:28,borderRadius:"50%",background:B.wasabi,color:B.nori,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:"0.75rem"},
  chipName:{fontSize:"0.82rem",fontWeight:600,color:B.kewpie},
  logoutBtn:{background:"none",border:"1px solid rgba(254,250,240,0.15)",borderRadius:999,padding:"5px 14px",fontSize:"0.75rem",fontWeight:600,color:B.kewpie,opacity:0.5,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  greetBox:{padding:"2rem 0 1.5rem"},greetHi:{fontFamily:"gelica,serif",fontSize:"2rem",fontWeight:700,color:B.kewpie,lineHeight:1.2},greetD:{fontSize:"0.88rem",color:B.kewpie,opacity:0.35,marginTop:4},
  // Tile grid — tablet-first, 3 cols capped at 960px, 4:3 tiles with vertically centred content
  grid:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,width:"100%",maxWidth:960,margin:"0 auto",flex:"0 0 auto"},
  card:{background:"rgba(212,251,111,0.06)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:16,padding:"1.4rem 1.4rem 1.3rem",textAlign:"left",cursor:"pointer",transition:"all 0.12s ease",display:"flex",flexDirection:"column",justifyContent:"center",fontFamily:"Manrope,sans-serif",animation:"fadeUp 0.35s ease both",position:"relative",aspectRatio:"4/3"},
  cardOff:{opacity:0.25,cursor:"default"},cTop:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12},cIcon:{fontSize:"1.75rem"},
  cSoon:{background:"rgba(254,250,240,0.08)",borderRadius:999,padding:"2px 6px",fontSize:"0.6rem",fontWeight:700,color:B.kewpie,opacity:0.5,textTransform:"uppercase"},
  cRolePill:{borderRadius:999,padding:"2px 7px",fontSize:"0.58rem",fontWeight:700,letterSpacing:"0.04em",textTransform:"uppercase",display:"inline-block"},
  cRoleFOH:{background:"rgba(212,251,111,0.15)",color:B.wasabi,border:"1px solid rgba(212,251,111,0.35)"},
  cRoleBOH:{background:"rgba(255,122,92,0.12)",color:B.coral,border:"1px solid rgba(255,122,92,0.35)"},
  cRoleBOTH:{background:"rgba(111,175,143,0.15)",color:B.eucalyptus,border:"1px solid rgba(111,175,143,0.4)"},
  cLabel:{fontFamily:"gelica,serif",fontSize:"1rem",fontWeight:700,color:B.wasabi,marginBottom:4,lineHeight:1.2},cDesc:{fontSize:"0.72rem",color:B.kewpie,opacity:0.45,lineHeight:1.4},
  hFoot:{textAlign:"center",color:B.kewpie,opacity:0.15,fontSize:"0.7rem",marginTop:"auto",paddingTop:16},

  // Shell
  shellRoot:{minHeight:"100vh",background:B.nori,fontFamily:"Manrope,sans-serif"},
  shellBar:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 20px",borderBottom:"1px solid rgba(212,251,111,0.1)"},
  shellBack:{background:"none",border:"none",color:B.wasabi,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  shellSiteFull:{fontSize:"0.72rem",fontWeight:700,color:B.wasabi,opacity:0.5,letterSpacing:"0.04em",textTransform:"uppercase"},
  // Sidebar (collapsible)
  sidebar:{width:200,borderRight:"1px solid rgba(212,251,111,0.08)",padding:"12px 8px",flexShrink:0,transition:"width 0.22s ease, padding 0.22s ease",overflow:"hidden"},
  sidebarCollapsed:{width:56,padding:"12px 6px"},
  sideToggle:{display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:32,borderRadius:8,border:"none",background:"none",color:B.kewpie,opacity:0.35,fontSize:"0.7rem",cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:10,transition:"opacity 0.15s ease"},
  sideItem:{display:"flex",alignItems:"center",width:"100%",padding:"8px 10px",borderRadius:10,border:"none",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.78rem",fontWeight:500,cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left",marginBottom:2,transition:"all 0.15s ease",whiteSpace:"nowrap"},
  sideItemCollapsed:{padding:"10px 0",justifyContent:"center"},
  sideItemActive:{background:"rgba(212,251,111,0.1)",color:B.wasabi,opacity:1,fontWeight:700},

  // Shared module styles
  content:{padding:"0 20px 24px",maxWidth:640,width:"100%",margin:"0 auto"},
  sectionCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"14px 16px",marginBottom:16},
  secLabel:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:2},secDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,lineHeight:1.5},
  secTitle:{fontSize:"0.7rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8,marginTop:8,display:"flex",alignItems:"center"},
  fieldLabel:{fontSize:"0.7rem",color:B.kewpie,opacity:0.4,marginBottom:3,display:"block"},
  input:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",outline:"none",boxSizing:"border-box"},
  textarea:{width:"100%",padding:"10px 12px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.85rem",outline:"none",resize:"none",minHeight:70,lineHeight:1.5,boxSizing:"border-box"},
  submitBtn:{width:"100%",padding:14,background:B.wasabi,color:B.nori,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:700,border:"none",borderRadius:14,cursor:"pointer",letterSpacing:"0.02em"},
  infoBanner:{padding:"10px 14px",borderRadius:10,border:"1px solid",marginBottom:12,fontSize:"0.78rem",lineHeight:1.5},

  // Rows
  row:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease"},
  soRow:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease"},
  itemName:{fontSize:"0.85rem",fontWeight:500,color:B.kewpie},
  // ctrls / cBtn / cVal / markBtn are defined once, in the forecast-tile block below.
  undoBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(255,122,92,0.2)",background:"rgba(255,122,92,0.06)",color:B.coral,fontSize:"0.78rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  restockBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(111,175,143,0.35)",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  receivedBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(111,175,143,0.35)",background:"rgba(111,175,143,0.12)",color:B.eucalyptus,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
  unclearedBox:{background:"rgba(212,251,111,0.03)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:12,padding:"10px 14px",marginBottom:8},

  // Stock categories
  catHeader:{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.08)",borderRadius:10,color:B.wasabi,fontSize:"0.82rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",marginBottom:2},

  // Modals — z-index must sit ABOVE the scanner's own .cc-modal (350) so the PIN modal
  // spawns on top of the scanner's Manual Lookup card, not behind it. 2026-05-07: bumped
  // 500 → 700 so PIN also stacks above DeliveryConfirmModal (z-600) and the Undo modal
  // (z-600) — was hiding the keypad behind the photo modal, making "Confirm delivery"
  // appear to do nothing.
  overlay:{position:"fixed",top:0,left:0,width:"100vw",height:"100vh",background:"rgba(0,0,0,0.6)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16,boxSizing:"border-box",overflowY:"auto"},
  modal:{background:B.nori,border:"1px solid rgba(212,251,111,0.2)",borderRadius:18,padding:20,maxWidth:380,width:"calc(100% - 32px)",maxHeight:"80vh",overflowY:"auto"},
  modalTitle:{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:B.wasabi,marginBottom:4},
  modalDesc:{fontSize:"0.8rem",color:B.kewpie,opacity:0.5,marginBottom:14},
  modalInput:{width:"100%",padding:"14px",background:"rgba(254,250,240,0.05)",border:"1px solid rgba(212,251,111,0.15)",borderRadius:10,color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"1.2rem",textAlign:"center",letterSpacing:"8px",outline:"none",marginBottom:8,boxSizing:"border-box"},
  modalErr:{fontSize:"0.78rem",color:B.coral,textAlign:"center",marginBottom:8},
  modalBtns:{display:"flex",gap:8},
  modalCancel:{flex:1,padding:12,borderRadius:10,border:"1px solid rgba(254,250,240,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",fontWeight:600,cursor:"pointer",fontFamily:"Manrope,sans-serif"},
  modalOk:{flex:1,padding:12,borderRadius:10,border:"none",background:B.wasabi,color:B.nori,fontSize:"0.85rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif"},

  // Loading
  loadingBox:{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 20px"},
  spinner:{width:32,height:32,border:"3px solid rgba(212,251,111,0.15)",borderTopColor:B.wasabi,borderRadius:"50%",animation:"spin 0.8s linear infinite"},
  emptyState:{textAlign:"center",padding:"60px 20px",fontSize:"0.85rem",color:B.kewpie,opacity:0.4},

  // Forecast tile — ported 1:1 from the Manchester kitchen app's ProductionForecast so the
  // Meadowhall Daily Forecast looks identical to the rest of the Rolled site-app suite.
  pendingWrap:{borderRadius:16,padding:"10px 10px 4px",marginBottom:8,transition:"box-shadow 0.45s ease",animation:"pendingPulse 3.2s ease-in-out infinite"},
  confirmedWrap:{borderRadius:16,padding:"10px 10px 4px",marginBottom:8,boxShadow:"0 0 0 1px rgba(111,175,143,0.22)",transition:"box-shadow 0.45s ease"},
  pendingPill:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.25)",color:B.coral,fontSize:"0.62rem",fontWeight:700,padding:"3px 10px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",marginLeft:10},
  pendingDot:{width:6,height:6,borderRadius:"50%",background:B.coral,boxShadow:"0 0 6px rgba(255,122,92,0.7)"},
  confirmedPill:{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(111,175,143,0.1)",border:"1px solid rgba(111,175,143,0.3)",color:B.eucalyptus,fontSize:"0.62rem",fontWeight:700,padding:"3px 10px",borderRadius:999,letterSpacing:"0.08em",textTransform:"uppercase",marginLeft:10},
  navBtn:{width:36,height:36,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.kewpie,opacity:0.5,fontSize:"0.85rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"},
  dateTrigger:{display:"inline-flex",alignItems:"center",gap:10,padding:"10px 18px",borderRadius:14,border:"1px solid rgba(212,251,111,0.18)",background:"rgba(212,251,111,0.05)",color:B.kewpie,cursor:"pointer",fontFamily:"Manrope,sans-serif",textAlign:"left",transition:"all 0.15s ease"},
  dateTriggerBadge:{fontSize:"0.6rem",fontWeight:800,color:B.wasabi,background:"rgba(212,251,111,0.12)",borderRadius:999,padding:"3px 10px",marginLeft:8,letterSpacing:"0.1em"},
  todayJumpBtn:{background:"none",border:"1px solid rgba(212,251,111,0.2)",color:B.wasabi,fontSize:"0.7rem",fontWeight:700,padding:"5px 14px",borderRadius:999,cursor:"pointer",fontFamily:"Manrope,sans-serif",letterSpacing:"0.06em",textTransform:"uppercase"},
  heroCard:{textAlign:"center",padding:"28px 20px",background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.12)",borderRadius:14,marginBottom:12,position:"relative"},
  siteSectionHeader:{fontFamily:"gelica,serif",fontSize:"1.3rem",fontWeight:700,color:B.wasabi,padding:"18px 14px 8px",borderTop:"2px solid rgba(212,251,111,0.15)",marginTop:10,marginBottom:4,letterSpacing:"0.01em",display:"flex",alignItems:"center",justifyContent:"center",gap:10,flexWrap:"wrap",rowGap:6,textAlign:"center"},
  siteSectionDot:{width:8,height:8,borderRadius:"50%",background:B.wasabi,boxShadow:"0 0 10px rgba(212,251,111,0.5)"},
  pill:{fontSize:"0.72rem",padding:"3px 8px",borderRadius:16,background:"rgba(212,251,111,0.06)",color:B.kewpie,opacity:0.6,display:"inline-flex",alignItems:"center",gap:4},
  pillDot:{width:4,height:4,borderRadius:"50%",background:B.wasabi},
  pillWarn:{background:"rgba(255,179,71,0.06)"},
  flag:{fontSize:"0.72rem",padding:"3px 8px",borderRadius:16,background:"rgba(212,251,111,0.06)",color:B.wasabi},
  flagsAppliedCard:{background:"rgba(212,251,111,0.04)",border:"1px solid rgba(212,251,111,0.1)",borderRadius:14,padding:"12px 16px 14px",marginBottom:16,position:"relative"},
  flagsAppliedBadge:{position:"absolute",top:-8,left:14,background:B.nori,padding:"0 8px",fontSize:"0.6rem",fontWeight:800,color:B.wasabi,letterSpacing:"0.1em",textTransform:"uppercase",border:"1px solid rgba(212,251,111,0.15)",borderRadius:8,lineHeight:"16px"},
  scaleRow:{display:"flex",justifyContent:"space-between",marginTop:6,padding:"0 2px",fontSize:"0.62rem",color:B.kewpie,opacity:0.35,fontFamily:"Manrope,sans-serif",fontWeight:600,letterSpacing:"0.04em"},
  scaleTick:{flex:1,textAlign:"center"},
  scaleTickEdge:{flex:"0 0 auto"},
  overrideHint:{fontSize:"0.7rem",color:B.kewpie,opacity:0.35,marginTop:8,textAlign:"center",lineHeight:1.5},
  skuColHeader:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 12px 10px",fontSize:"0.62rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"0.1em",textTransform:"uppercase",borderBottom:"1px solid rgba(212,251,111,0.08)",marginBottom:6},
  skuColHeaderLeft:{flex:1},
  skuColHeaderRolls:{width:140,textAlign:"center"},
  skuColHeaderPortions:{width:60,textAlign:"right"},
  skuRow:{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",background:"rgba(254,250,240,0.03)",border:"1px solid rgba(212,251,111,0.06)",borderRadius:10,marginBottom:4,transition:"all 0.15s ease",gap:8},
  skuRowLeft:{flex:1,minWidth:0},
  skuPortionsVal:{width:60,textAlign:"right",fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.9rem",color:B.kewpie,opacity:0.55},
  ctrls:{display:"flex",alignItems:"center",gap:8},
  cBtn:{width:30,height:30,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.15)",background:"none",color:B.wasabi,fontSize:"1.1rem",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Manrope,sans-serif"},
  cVal:{fontFamily:"Manrope,sans-serif",fontWeight:700,fontSize:"0.95rem",color:B.kewpie,opacity:0.4,minWidth:24,textAlign:"center"},
  calcRow:{display:"flex",justifyContent:"space-between",alignItems:"center"},
  calWeekday:{fontSize:"0.68rem",fontWeight:700,color:B.kewpie,opacity:0.4,textAlign:"center",letterSpacing:"0.05em",textTransform:"uppercase",padding:"4px 0"},
  calDay:{aspectRatio:"1",borderRadius:10,border:"1px solid transparent",background:"none",color:B.kewpie,fontFamily:"Manrope,sans-serif",fontSize:"0.9rem",fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.1s ease"},
  calDayToday:{background:"rgba(212,251,111,0.08)",border:"1px solid rgba(212,251,111,0.25)",fontWeight:700,color:B.wasabi},
  calDaySelected:{background:B.wasabi,color:B.nori,fontWeight:800,border:"1px solid "+B.wasabi,boxShadow:"0 0 18px rgba(212,251,111,0.4)"},
  markBtn:{padding:"8px 16px",borderRadius:10,border:"1px solid rgba(212,251,111,0.25)",background:"rgba(212,251,111,0.08)",color:B.wasabi,fontSize:"0.78rem",fontWeight:700,cursor:"pointer",fontFamily:"Manrope,sans-serif",whiteSpace:"nowrap"},
};

function MeadowhallForecast({backend,requestPin}){
  const [fcDate,setFcDate] = useState(new Date());
  const [pickerOpen,setPickerOpen] = useState(false);
  const dateStr = useMemo(()=>{
    const y=fcDate.getFullYear(),m=String(fcDate.getMonth()+1).padStart(2,"0"),d=String(fcDate.getDate()).padStart(2,"0");
    return y+"-"+m+"-"+d;
  },[fcDate]);
  const todayStr = useMemo(()=>{
    const t=new Date(),y=t.getFullYear(),m=String(t.getMonth()+1).padStart(2,"0"),d=String(t.getDate()).padStart(2,"0");
    return y+"-"+m+"-"+d;
  },[]);
  const isToday = dateStr===todayStr;
  const dayName = fcDate.toLocaleDateString("en-GB",{weekday:"long"});
  const dateDisplay = fcDate.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});
  const changeDate = (delta)=>{setFcDate(d=>{const n=new Date(d);n.setDate(n.getDate()+delta);return n})};

  return (
    <div style={S.content}>
      {/* Shared date nav — arrows for ±1 day, tap to open calendar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:8}}>
        <button style={S.navBtn} onClick={()=>changeDate(-1)} aria-label="Previous day">◀</button>
        <button onClick={()=>setPickerOpen(true)} style={S.dateTrigger} aria-label="Open calendar">
          <span style={{fontSize:"1rem",lineHeight:1,marginRight:4}}>📅</span>
          <span>
            <span style={{display:"block",fontFamily:"gelica,serif",fontWeight:700,fontSize:"1.1rem",color:B.kewpie,lineHeight:1.1}}>{dayName}</span>
            <span style={{display:"block",fontSize:"0.78rem",color:B.kewpie,opacity:0.45,marginTop:2}}>{dateDisplay}</span>
          </span>
          {isToday && <span style={S.dateTriggerBadge}>TODAY</span>}
        </button>
        <button style={S.navBtn} onClick={()=>changeDate(1)} aria-label="Next day">▶</button>
      </div>
      {!isToday && (
        <div style={{textAlign:"center",marginBottom:6}}>
          <button style={S.todayJumpBtn} onClick={()=>setFcDate(new Date())}>Jump to today</button>
        </div>
      )}

      {pickerOpen && <ForecastDatePickerModal value={fcDate} onChange={setFcDate} onClose={()=>setPickerOpen(false)}/>}

      <MeadowhallForecastSection backend={backend} requestPin={requestPin} date={fcDate} dateStr={dateStr}/>
    </div>
  );
}

function ForecastDatePickerModal({value,onChange,onClose}){
  const [viewDate,setViewDate] = useState(()=>{ const d=new Date(value); d.setDate(1); return d; });
  const monthLabel = viewDate.toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  const firstOfMonth = new Date(viewDate.getFullYear(),viewDate.getMonth(),1);
  const leadMondayOffset = (firstOfMonth.getDay()+6)%7;
  const gridStart = new Date(firstOfMonth); gridStart.setDate(gridStart.getDate()-leadMondayOffset);
  const days = Array.from({length:42},(_,i)=>{ const d=new Date(gridStart); d.setDate(gridStart.getDate()+i); return d; });
  const sameDay=(a,b)=>a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();
  const today = new Date();
  const prevMonth=()=>{const d=new Date(viewDate);d.setMonth(d.getMonth()-1);setViewDate(d);};
  const nextMonth=()=>{const d=new Date(viewDate);d.setMonth(d.getMonth()+1);setViewDate(d);};
  const pick=(d)=>{onChange(d);onClose();};
  const weekdays = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={{...S.modal,maxWidth:400}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <button style={S.navBtn} onClick={prevMonth} aria-label="Previous month">◀</button>
          <div style={{fontFamily:"gelica,serif",fontSize:"1.15rem",fontWeight:700,color:B.wasabi}}>{monthLabel}</div>
          <button style={S.navBtn} onClick={nextMonth} aria-label="Next month">▶</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
          {weekdays.map((w,i)=>(<div key={i} style={S.calWeekday}>{w}</div>))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
          {days.map((d,i)=>{
            const inMonth = d.getMonth()===viewDate.getMonth();
            const dToday = sameDay(d,today);
            const dSelected = sameDay(d,value);
            return (
              <button key={i} onClick={()=>pick(d)} style={{...S.calDay,...(inMonth?{}:{opacity:0.22}),...(dToday&&!dSelected?S.calDayToday:{}),...(dSelected?S.calDaySelected:{})}}>{d.getDate()}</button>
            );
          })}
        </div>
        <div style={{display:"flex",gap:8,marginTop:16}}>
          <button style={S.modalCancel} onClick={onClose}>Cancel</button>
          <button style={S.modalOk} onClick={()=>pick(new Date())}>Today</button>
        </div>
      </div>
    </div>
  );
}

function MeadowhallForecastSection({backend,requestPin,date,dateStr}){
  const [forecast,setForecast] = useState(null);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState(null);
  const [showInfo,setShowInfo] = useState(false);
  const [override,setOverride] = useState(0);
  const [skuAdj,setSkuAdj] = useState({});
  const [confirmed,setConfirmed] = useState(false);
  const [confirming,setConfirming] = useState(false);
  const [undoing,setUndoing] = useState(false);
  // Inline error after a failed undo (e.g. role gate rejection). See kitchen
  // app — same silent-loss prevention.
  const [undoErr,setUndoErr] = useState("");
  const [reasonModal,setReasonModal] = useState(false);
  const [reasonText,setReasonText] = useState("");
  const [reasonErr,setReasonErr] = useState(false);
  // Has today's forecast already been confirmed by anyone? Null = not checked
  // yet, object = state from backend. Shapes:
  //   { confirmed: true, by, at, portions, rolls, resubmit? }
  //   { confirmed: false, undone: true, undoneBy, undoneAt, previousPortions, previousRolls }
  //   { confirmed: false }
  // false (not null) means we successfully checked and it's pending.
  const [confirmedInfo,setConfirmedInfo] = useState(null);
  // Yesterday's wastage total — comes from the Manchester backend, not Adam's
  // Meadowhall forecast script (which has no view onto the Daily Reports sheet).
  const [previousDayWaste,setPreviousDayWaste] = useState(null);

  // Re-fetch the confirmedInfo banner state without remounting the form so
  // chefs don't lose in-progress slider/SKU tweaks. Used after a same-session
  // undo so the banner flips from green-confirmed to yellow-undone notice.
  const reloadStatus = useCallback(()=>{
    const dateBE = date ? date.toLocaleDateString("en-GB") : "";
    if (!dateBE) return;
    fetch(BACKEND + "?action=forecaststatus&site=meadowhall&date=" + encodeURIComponent(dateBE))
      .then(r => r.ok ? r.json() : {confirmed:false})
      .then(data => { setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); })
      .catch(() => { setConfirmedInfo(false); });
  },[date]);

  useEffect(()=>{
    let cancelled=false;
    setLoading(true);setError(null);setForecast(null);setShowInfo(false);setOverride(0);setSkuAdj({});setConfirmed(false);setConfirmedInfo(null);setPreviousDayWaste(null);
    fetch(backend+"?action=forecast&date="+dateStr)
      .then(r=>{if(!r.ok)throw new Error("HTTP "+r.status);return r.json()})
      .then(data=>{if(!cancelled){if(data.error)throw new Error(data.error);setForecast(stripRetiredRolls(data));setLoading(false)}})
      .catch(e=>{if(!cancelled){setError(e.message);setLoading(false)}});
    // Check the my-backend forecastStatus endpoint in parallel: has anyone
    // already confirmed today's forecast? Adam's endpoint doesn't surface this.
    const dateBE = date ? date.toLocaleDateString("en-GB") : "";
    if (dateBE) {
      fetch(BACKEND + "?action=forecaststatus&site=meadowhall&date=" + encodeURIComponent(dateBE))
        .then(r => r.ok ? r.json() : {confirmed:false})
        .then(data => { if(!cancelled) setConfirmedInfo(data && (data.confirmed || data.undone) ? data : false); })
        .catch(() => { if(!cancelled) setConfirmedInfo(false); });
    }
    // Previous day wastage lives on the Manchester backend (reads Meadowhall's
    // Daily Reports sheet). Adam's Meadowhall forecast script has no sheet
    // access so we fetch it here in parallel. forDate anchors the lookup to
    // the forecast day being viewed — so scrolling the calendar shows
    // wastage from the day BEFORE that, not always "yesterday relative to
    // right now" (the 2026-04-23 bug Adam hit).
    fetch(BACKEND + "?action=previousdaywaste&site=meadowhall&forDate=" + encodeURIComponent(dateStr))
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data && !data.error) setPreviousDayWaste(data); })
      .catch(() => {});
    return()=>{cancelled=true};
  },[dateStr,backend,date]);

  const calcTarget = ()=>{
    if(!forecast||!forecast.forecast)return 0;
    let b = Number(forecast.forecast.weightedBase)||0, am=1;
    (forecast.autoFlags||[]).forEach(f=>{am+=Number(f.mult)||0});
    let adj = b*am;
    if(forecast.wastageAdjustment){adj*=(forecast.wastageAdjustment.type==="trim"?0.95:1.05)}
    adj *= (1+override/100);
    const TB = Number(forecast.forecast.tgtgBuffer)||0;
    let tp = Math.round(adj+TB);
    if(forecast.forecast.minApplied && override===0) tp = forecast.forecast.totalPortions;
    return tp;
  };
  const target = calcTarget();

  // Largest-remainder allocation: rolls sum exactly to target/2, so hero portions = backend target.
  const baseRollsBySku = useMemo(()=>{
    if(!forecast||!forecast.skuBreakdown)return {};
    const skus = forecast.skuBreakdown;
    const targetRolls = Math.round(target/2);
    const raw = skus.map(s=>{
      const pct = (s.pct<=1)?s.pct*100:s.pct;
      return target*(pct/100)/2;
    });
    const floors = raw.map(r=>Math.floor(r));
    const out = {};
    skus.forEach((s,i)=>{out[s.name]=floors[i]});
    let need = targetRolls - floors.reduce((a,b)=>a+b,0);
    if(need>0){
      const rem = raw.map((r,i)=>({r:r-floors[i],i})).sort((a,b)=>b.r-a.r);
      for(let k=0;k<Math.min(need,rem.length);k++) out[skus[rem[k].i].name]++;
    } else if(need<0){
      const rem = raw.map((r,i)=>({r:r-floors[i],i})).sort((a,b)=>a.r-b.r);
      for(let k=0;k<Math.min(-need,rem.length);k++){
        const n=skus[rem[k].i].name;
        if(out[n]>0)out[n]--;
      }
    }
    return out;
  },[forecast,target]);

  const getSkuBase = (name)=>baseRollsBySku[name]||0;

  const totalRolls = Object.values(baseRollsBySku).reduce((a,b)=>a+b,0) + Object.values(skuAdj).reduce((a,b)=>a+b,0);
  const totalPortions = totalRolls*2;

  const adjSku = (name,delta)=>{
    setSkuAdj(prev=>{
      const base = getSkuBase(name);
      const cur = prev[name]||0;
      if(base+cur+delta<0)return prev;
      return {...prev,[name]:cur+delta};
    });
  };

  // Phase 5 Option 2 (2026-04-21): capture a full calculation snapshot so the
  // admin Forecast Submissions drill-down can show the exact roll mix the
  // staff member saw at confirm time. Meadowhall's primary confirm POST goes
  // to Adam's standalone backend (above) — the snapshot rides in a parallel
  // fire-and-forget POST to the Manchester backend's forecastConfirm action,
  // which writes into Meadowhall's per-site ForecastConfirmations tab.
  const buildSnapshot = ()=>{
    if (!forecast) return null;
    const finalSkuRolls = {};
    Object.keys(baseRollsBySku).forEach(k => {
      finalSkuRolls[k] = (baseRollsBySku[k]||0) + (skuAdj[k]||0);
    });
    Object.keys(skuAdj).forEach(k => { if (!(k in finalSkuRolls)) finalSkuRolls[k] = skuAdj[k]; });
    const skuPcts = {};
    (forecast.skuBreakdown||[]).forEach(s => {
      skuPcts[s.name] = (s.pct <= 1) ? s.pct * 100 : s.pct;
    });
    const fc = forecast.forecast || {};
    const range = (fc.rangeLow != null && fc.rangeHigh != null)
      ? {low: fc.rangeLow, high: fc.rangeHigh, safetyBuffer: fc.safetyBuffer != null ? fc.safetyBuffer : null}
      : null;
    return {
      weightedBase: fc.weightedBase,
      basePredictedPortions: fc.totalPortions,
      tgtgBuffer: fc.tgtgBuffer,
      minApplied: !!fc.minApplied,
      autoFlags: (forecast.autoFlags||[]).map(f => ({label:f.label, mult:f.mult, emoji:f.emoji||"", positive:!!f.positive})),
      wastageAdjustment: forecast.wastageAdjustment ? forecast.wastageAdjustment.type : null,
      wastageAdjustmentDetail: forecast.wastageAdjustment || null,
      overridePct: override,
      confirmedPortions: totalPortions,
      confirmedRolls: totalRolls,
      baseSkuRolls: Object.assign({}, baseRollsBySku),
      userSkuAdjustments: Object.assign({}, skuAdj),
      finalSkuRolls,
      skuPcts,
      inputs: forecast.inputs || null,
      calc: forecast.calc || null,
      range,
      schemaVersion: 1
    };
  };

  // 2026-05-04 (optimistic): banner state flips on tap, the two POSTs (Adam's
  // primary + Manchester snapshot) run in background. JJ flagged the prior
  // wait-then-flip as feeling broken — the network round-trip was visible to
  // staff and looked like the page was hanging.
  const doConfirm = (overrideNote)=>{
    requestPin(async(staffName,staffPin)=>{
      const priorInfo = confirmedInfo;
      const priorConfirmed = confirmed;
      const wasUndone = !!(priorInfo && priorInfo.undone);
      // Apply confirmed state immediately.
      setConfirmedInfo({
        confirmed: true,
        by: staffName,
        at: new Date().toISOString(),
        portions: totalPortions,
        rolls: totalRolls,
        resubmit: wasUndone
      });
      setConfirmed(true);
      setConfirming(true);
      setUndoErr("");
      const body = {
        date: forecast.date||dateStr,
        day: forecast.day||"",
        footfall: forecast.footfall!=null?forecast.footfall:(forecast.inputs&&forecast.inputs.footfall!=null?forecast.inputs.footfall:""),
        lastSameDay: forecast.inputs&&forecast.inputs.lastSameDay!=null?forecast.inputs.lastSameDay:"baseline",
        sixWeekAvg: forecast.inputs&&forecast.inputs.sixWeekAvg!=null?forecast.inputs.sixWeekAvg:"baseline",
        lastWastage: forecast.inputs&&forecast.inputs.lastWastage!=null?forecast.inputs.lastWastage:"",
        soldOut: (forecast.inputs&&forecast.inputs.soldOut)?"Yes":"No",
        autoFlags: (forecast.autoFlags||[]).map(f=>f.label).join(", ")||"None",
        manualOverrides: overrideNote,
        forecastPortions: totalPortions,
        forecastRolls: totalRolls,
        staffName: staffName||"Unknown"
      };
      // Adam's Meadowhall backend stays mode:"no-cors" — it drives his Phase
      // 3 logic and we don't read its response. Failure here is rare and
      // doesn't block the snapshot side-channel below.
      try{await fetch(backend,{method:"POST",mode:"no-cors",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)})}catch(e){}
      // Manchester snapshot — fire-and-forget. text/plain (no preflight)
      // so role-gate rejections are at least readable on retry.
      try{
        const snapshotBody = {
          action: "forecastConfirm",
          site: "meadowhall",
          date: (forecast.date||dateStr),
          totalPortions, totalRolls,
          override: (override===0 ? 0 : (override>0?"+"+override+"%":override+"%")),
          skuAdjustments: skuAdj,
          snapshot: buildSnapshot(),
          staffName: staffName||"Unknown",
          staffPin
        };
        fetch(BACKEND, {method:"POST", headers:{"Content-Type":"text/plain;charset=utf-8"}, body:JSON.stringify(snapshotBody)}).catch(()=>{});
      }catch(e){ /* snapshot is a side-channel; never block confirm on it */ }
      setConfirming(false);
      // No reloadStatus — the optimistic state IS the truth until next
      // page load. If the primary POST silently failed, the next load
      // will show pending and the chef can re-confirm; not ideal, but
      // matches how confirm has always behaved on Meadowhall (no-cors).
    });
  };

  const handleConfirm = ()=>{
    if(override!==0){setReasonText("");setReasonErr(false);setReasonModal(true);return}
    doConfirm("None");
  };

  // Undo & resubmit (2026-05-04, optimistic). Banner flips to yellow
  // "previously undone" the instant the user taps; POST runs in background;
  // rollback + inline error on failure. Server enforces BOH/Admin role via
  // canPerformAction.
  const handleUndo = ()=>{
    setUndoErr("");
    requestPin(async(staffName,staffPin)=>{
      const priorInfo = confirmedInfo;
      const priorConfirmed = confirmed;
      const undonePortions = (priorInfo && priorInfo.portions != null) ? priorInfo.portions : totalPortions;
      const undoneRolls    = (priorInfo && priorInfo.rolls    != null) ? priorInfo.rolls    : totalRolls;
      // Optimistically flip to undone state.
      setConfirmedInfo({
        confirmed: false,
        undone: true,
        undoneBy: staffName,
        undoneAt: new Date().toISOString(),
        previousPortions: undonePortions,
        previousRolls: undoneRolls
      });
      setConfirmed(false);
      setUndoing(true);
      setUndoErr("");
      // Background POST. text/plain skips the CORS preflight Apps Script
      // doesn't answer; response still readable so role-gate rejections
      // surface (memory: apps_script_cors_preflight_trap).
      let ok = false, errMsg = "";
      try{
        const r = await fetch(BACKEND, {
          method:"POST",
          headers:{"Content-Type":"text/plain;charset=utf-8"},
          body: JSON.stringify({
            action:"forecastUndo",
            site:"meadowhall",
            date: (forecast && forecast.date) || dateStr,
            undonePortions, undoneRolls, undoneOverride: override,
            staffName, staffPin
          })
        });
        const result = await r.json().catch(()=>null);
        if (r.ok && result && result.status === "success") ok = true;
        else errMsg = (result && (result.error || result.message)) || ("HTTP " + r.status);
      }catch(e){ errMsg = e.message || "Network error"; }
      setUndoing(false);
      if (!ok) {
        // Roll back the optimistic flip.
        setConfirmedInfo(priorInfo);
        setConfirmed(priorConfirmed);
        setUndoErr(errMsg || "Couldn't undo — try again or check with admin.");
      }
    });
  };

  const submitReason = ()=>{
    const r = reasonText.trim();
    if(!r){setReasonErr(true);return}
    setReasonModal(false);
    doConfirm(((override>0?"+":"")+override+"% — "+r));
  };

  const retry = ()=>{
    setLoading(true);setError(null);
    fetch(backend+"?action=forecast&date="+dateStr)
      .then(r=>r.json()).then(data=>{setForecast(stripRetiredRolls(data));setLoading(false)}).catch(e=>{setError(e.message);setLoading(false)});
  };

  const isRemotelyConfirmed = !!(confirmedInfo && confirmedInfo.confirmed);
  const isConfirmed = confirmed || isRemotelyConfirmed;
  const showPending = !!forecast && !loading && !error && !isConfirmed;
  const showConfirmed = !!forecast && !loading && !error && isConfirmed;

  const fmtConfirmedAt = (iso) => {
    try { return new Date(iso).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}); }
    catch(e) { return ""; }
  };

  return (
    <div>
      <div style={S.siteSectionHeader}>
        <span style={S.siteSectionDot}/>
        Meadowhall
        {showPending && <span style={S.pendingPill}><span style={S.pendingDot}/>Awaiting confirmation</span>}
        {showConfirmed && <span style={S.confirmedPill}>✓ Confirmed</span>}
      </div>

      {/* Confirmed banner — fires whether the lock-in came from this session
          or another tab/device. Undo & resubmit gives staff an escape hatch
          when the forecast turns out wrong; gated server-side via
          canPerformAction (BOH + Admin only). */}
      {isConfirmed && (
        <div style={{background:"rgba(111,175,143,0.12)",border:"1px solid rgba(111,175,143,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:12,color:"#9DD0B0",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:"1.4rem",flexShrink:0}}>✓</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"0.88rem",color:B.kewpie,letterSpacing:"0.01em"}}>
              {confirmedInfo && confirmedInfo.resubmit
                ? "Forecast resubmitted"
                : (isRemotelyConfirmed ? "Forecast already confirmed" : "Forecast confirmed")}
              {confirmedInfo && confirmedInfo.by ? " by " + confirmedInfo.by : ""}
              {confirmedInfo && confirmedInfo.at ? " at " + fmtConfirmedAt(confirmedInfo.at) : ""}
            </div>
            <div style={{fontSize:"0.75rem",opacity:0.75,marginTop:2}}>
              {confirmedInfo && confirmedInfo.portions != null
                ? <>Locked in at <strong>{confirmedInfo.portions} portions</strong>{confirmedInfo.rolls != null && <> / {confirmedInfo.rolls} rolls</>}. </>
                : null}
              Mistake? <strong>Undo &amp; resubmit</strong> to edit and re-confirm.
            </div>
          </div>
          <button
            onClick={handleUndo}
            disabled={undoing}
            style={{flexShrink:0,padding:"8px 14px",borderRadius:10,border:"1px solid rgba(255,179,71,0.4)",background:"rgba(255,179,71,0.08)",color:B.sunshine,fontFamily:"inherit",fontSize:"0.78rem",fontWeight:700,letterSpacing:"0.02em",cursor:undoing?"wait":"pointer"}}
          >
            {undoing?"Undoing…":"↩ Undo & resubmit"}
          </button>
          {undoErr && (
            <div style={{flexBasis:"100%",fontSize:"0.78rem",color:B.coral,marginTop:6,padding:"8px 10px",borderRadius:8,background:"rgba(255,122,92,0.08)",border:"1px solid rgba(255,122,92,0.3)"}}>
              Undo failed: {undoErr}
            </div>
          )}
        </div>
      )}

      {!isConfirmed && confirmedInfo && confirmedInfo.undone && (
        <div style={{background:"rgba(255,179,71,0.07)",border:"1px solid rgba(255,179,71,0.3)",borderRadius:12,padding:"14px 16px",marginBottom:12,color:B.sunshine,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:"1.4rem",flexShrink:0}}>↩</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:"0.88rem",color:B.kewpie,letterSpacing:"0.01em"}}>
              Last submission undone{confirmedInfo.undoneBy?" by "+confirmedInfo.undoneBy:""}{confirmedInfo.undoneAt?" at "+fmtConfirmedAt(confirmedInfo.undoneAt):""}
            </div>
            <div style={{fontSize:"0.75rem",opacity:0.85,marginTop:2}}>
              {confirmedInfo.previousPortions!=null && <>Was <strong>{confirmedInfo.previousPortions} portions</strong>{confirmedInfo.previousRolls!=null && <> / {confirmedInfo.previousRolls} rolls</>}. </>}
              Review the numbers and confirm again.
            </div>
          </div>
        </div>
      )}

      {loading && <div style={S.loadingBox}><div style={S.spinner}/><div style={{marginTop:12,color:B.kewpie,opacity:0.4,fontSize:"0.82rem"}}>Loading forecast…</div></div>}
      {error && <div style={{...S.sectionCard,borderColor:"rgba(255,122,92,0.3)"}}><div style={{color:B.coral,fontSize:"0.85rem",fontWeight:600}}>Failed to load forecast</div><div style={{color:B.kewpie,opacity:0.5,fontSize:"0.78rem",marginTop:4}}>{error}</div><button style={{...S.markBtn,marginTop:10}} onClick={retry}>Retry</button></div>}

      {forecast && !loading && !error && (<div style={isConfirmed?S.confirmedWrap:S.pendingWrap}>
        {/* Hero — live total from per-SKU commitments */}
        <div style={S.heroCard}>
          <div style={{fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"3px",textTransform:"uppercase",marginBottom:4}}>TOTAL PORTIONS</div>
          <div style={{fontFamily:"gelica,serif",fontSize:"3.5rem",fontWeight:700,color:B.wasabi,lineHeight:1,letterSpacing:"-1px"}}>{totalPortions}</div>
          <div style={{fontSize:"0.82rem",color:B.kewpie,opacity:0.5,marginTop:4}}>{totalRolls} rolls to make</div>
          {forecast.forecast.tgtgBuffer!=null && <div style={{fontSize:"0.7rem",color:B.kewpie,opacity:0.3,marginTop:6}}>Incl. {forecast.forecast.tgtgBuffer} TGTG buffer</div>}
          <button style={{position:"absolute",top:10,right:10,width:24,height:24,borderRadius:"50%",border:"1px solid rgba(212,251,111,0.2)",background:"none",color:B.kewpie,opacity:0.4,fontSize:"0.7rem",fontStyle:"italic",cursor:"pointer"}} onClick={()=>setShowInfo(!showInfo)}>i</button>
        </div>

        {/* Data sources */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>DATA SOURCES</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
            {forecast.inputs && forecast.inputs.footfall!=null && <span style={S.pill}><span style={S.pillDot}/> Footfall: <strong>{forecast.inputs.footfall}</strong></span>}
            {forecast.footfall!=null && !(forecast.inputs && forecast.inputs.footfall!=null) && <span style={S.pill}><span style={S.pillDot}/> Footfall: <strong>{forecast.footfall}</strong></span>}
            {forecast.inputs && forecast.inputs.lastSameDay!=null
              ? <span style={S.pill}><span style={S.pillDot}/> Last {forecast.day}: <strong>{forecast.inputs.lastSameDay}</strong></span>
              : <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> Last {forecast.day||"same-day"}: <strong>baseline</strong></span>}
            {forecast.inputs && forecast.inputs.sixWeekAvg!=null
              ? <span style={S.pill}><span style={S.pillDot}/> 6wk avg: <strong>{forecast.inputs.sixWeekAvg}</strong>{forecast.inputs.weeksOfData?" ("+forecast.inputs.weeksOfData+"wk)":""}</span>
              : <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> 6wk avg: <strong>baseline</strong></span>}
            {forecast.inputs && forecast.inputs.lastWastage>0 && <span style={S.pill}><span style={S.pillDot}/> Wastage: <strong>{forecast.inputs.lastWastage}%</strong></span>}
            {forecast.inputs && forecast.inputs.soldOut && <span style={{...S.pill,...S.pillWarn}}><span style={{...S.pillDot,background:B.sunshine}}/> Sold out last time</span>}
          </div>
        </div>

        {/* Previous day wastage — read-only echo of yesterday's EOD total
            with per-SKU mix. Sourced from the Manchester backend via
            previousDayWaste state (Adam's Meadowhall forecast script
            doesn't expose it). */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={S.secTitle}>PREVIOUS DAY WASTAGE <span style={{marginLeft:6,color:B.kewpie,opacity:0.3,fontWeight:400,textTransform:"none",letterSpacing:0}}>(read-only)</span></div>
          {previousDayWaste && previousDayWaste.totalPortions!=null ? (
            <>
              <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap",marginBottom:previousDayWaste.items&&previousDayWaste.items.length?10:0}}>
                <span style={{fontFamily:"gelica,serif",fontSize:"1.8rem",fontWeight:700,color:previousDayWaste.totalPortions===0?B.wasabi:B.kewpie,lineHeight:1}}>{previousDayWaste.totalPortions}</span>
                <span style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.5}}>portion{previousDayWaste.totalPortions===1?"":"s"} wasted on {previousDayWaste.date}</span>
              </div>
              {previousDayWaste.items && previousDayWaste.items.length > 0 && (
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {previousDayWaste.items.map(it=>(
                    <span key={it.name} style={S.pill}><span style={S.pillDot}/> {it.name}: <strong>{it.qty}</strong></span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.4,fontStyle:"italic"}}>No EOD report submitted yesterday</div>
          )}
        </div>

        {/* Context flags — already applied to the total above */}
        {forecast.autoFlags && forecast.autoFlags.length>0 && (
          <div style={S.flagsAppliedCard}>
            <div style={S.flagsAppliedBadge}>✓ Already applied</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.55,marginBottom:8,marginTop:4,lineHeight:1.5}}>These factors are baked into the total above — no further action needed.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
              {forecast.autoFlags.map((f,i)=>(
                <span key={i} style={{...S.flag,...(f.positive?{}:{background:"rgba(255,179,71,0.06)",color:B.sunshine})}}>{f.emoji||""} {f.label} {Number(f.mult)>0?"+":""}{Math.round((Number(f.mult)||0)*100)}%</span>
              ))}
              {override!==0&&<span style={S.flag}>🔧 Override {override>0?"+":""}{override}%</span>}
            </div>
          </div>
        )}

        {/* Wastage warning */}
        {forecast.wastageAdjustment && (
          <div style={{...S.infoBanner,borderColor:forecast.wastageAdjustment.type==="trim"?"rgba(255,122,92,0.2)":"rgba(212,251,111,0.15)",background:forecast.wastageAdjustment.type==="trim"?"rgba(255,122,92,0.05)":"rgba(212,251,111,0.04)",color:forecast.wastageAdjustment.type==="trim"?B.coral:B.wasabi}}>
            {forecast.wastageAdjustment.type==="trim"?"⚠":"ℹ️"} {forecast.wastageAdjustment.message}
          </div>
        )}

        {/* Min floor notice */}
        {forecast.forecast.minApplied && override===0 && (
          <div style={{...S.infoBanner,borderColor:"rgba(212,251,111,0.12)",background:"rgba(212,251,111,0.04)",color:B.wasabi}}>
            ℹ️ Minimum floor applied ({forecast.forecast.totalPortions} portions)
          </div>
        )}

        {/* Info panel */}
        {showInfo && forecast.calc && (
          <div style={{...S.sectionCard,marginBottom:8}}>
            <div style={S.secTitle}>MODEL BREAKDOWN</div>
            <div style={{fontSize:"0.78rem",color:B.kewpie,opacity:0.6,lineHeight:2}}>
              {Object.keys(forecast.calc).filter(k=>k.endsWith("Signal")).map(sigKey=>{
                const wKey = sigKey.replace(/Signal$/,"Weight");
                let rawLabel = sigKey.replace(/Signal$/,"").replace(/([A-Z])/g," $1").trim();
                const label = rawLabel.length>0 ? rawLabel[0].toUpperCase()+rawLabel.slice(1) : sigKey;
                const weight = forecast.calc[wKey];
                return (
                  <div key={sigKey} style={S.calcRow}><span>{label}{weight?" (×"+weight+")":""}</span><strong style={{color:B.kewpie}}>{forecast.calc[sigKey]}</strong></div>
                );
              })}
              <div style={{...S.calcRow,borderTop:"1px solid rgba(212,251,111,0.1)",paddingTop:4,color:B.wasabi}}><span>Weighted base</span><strong>{forecast.forecast.weightedBase}</strong></div>
              {forecast.forecast.tgtgBuffer!=null && <div style={S.calcRow}><span>TGTG buffer</span><strong>+{forecast.forecast.tgtgBuffer}</strong></div>}
              <div style={{...S.calcRow,color:B.wasabi}}><span>Total</span><strong>{totalPortions}</strong></div>
            </div>
          </div>
        )}

        {/* Override slider — 1% steps, ruled scale, direction labels */}
        <div style={{...S.sectionCard,marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:"0.65rem",fontWeight:700,color:B.kewpie,opacity:0.35,letterSpacing:"2px",textTransform:"uppercase"}}>MANUAL OVERRIDE</span>
            <span style={{fontFamily:"gelica,serif",fontSize:"1.1rem",fontWeight:700,color:override!==0?B.wasabi:B.kewpie,opacity:override!==0?1:0.3,transition:"color 0.2s"}}>{override>0?"+":""}{override}%</span>
          </div>
          <input type="range" min="-30" max="30" step="1" value={override} onChange={e=>setOverride(Number(e.target.value))} style={{width:"100%",accentColor:B.wasabi,display:"block"}}/>
          <div style={S.scaleRow}>
            <span style={S.scaleTickEdge}>−30%</span>
            <span style={S.scaleTick}>−15%</span>
            <span style={S.scaleTick}>0</span>
            <span style={S.scaleTick}>+15%</span>
            <span style={S.scaleTickEdge}>+30%</span>
          </div>
          <div style={S.overrideHint}>← less than forecast &nbsp;·&nbsp; more than forecast →</div>
        </div>

        {/* SKU breakdown */}
        {forecast.skuBreakdown && forecast.skuBreakdown.length>0 && (
          <div style={{...S.sectionCard,marginBottom:8}}>
            <div style={S.secTitle}>PER-SKU BREAKDOWN</div>
            <div style={{fontSize:"0.72rem",color:B.kewpie,opacity:0.4,marginBottom:10,lineHeight:1.5}}>Adjust rolls (1 roll = 2 portions). The total above updates live.</div>
            <div style={S.skuColHeader}>
              <span style={S.skuColHeaderLeft}>Roll type</span>
              <span style={S.skuColHeaderRolls}>Rolls</span>
              <span style={S.skuColHeaderPortions}>Portions</span>
            </div>
            {forecast.skuBreakdown.map(s=>{
              const base=getSkuBase(s.name);
              const adj=skuAdj[s.name]||0;
              const rolls=base+adj;
              const portions=rolls*2;
              const pctDisplay = s.pct<=1 ? (s.pct*100).toFixed(1) : s.pct.toFixed(1);
              return (
                <div key={s.name} style={S.skuRow}>
                  <div style={S.skuRowLeft}>
                    <span style={S.itemName||{fontSize:"0.9rem",color:B.kewpie,fontWeight:500}}>{s.name}</span>
                    {s.isSpecial&&<span style={{fontSize:"0.55rem",color:B.sunshine,fontWeight:700,marginLeft:6,letterSpacing:"1px"}}>NEW</span>}
                    <div style={{fontSize:"0.68rem",color:B.kewpie,opacity:0.3}}>{pctDisplay}%</div>
                  </div>
                  <div style={{...S.ctrls,width:140,justifyContent:"center"}}>
                    <button style={S.cBtn} onClick={()=>adjSku(s.name,-1)}>−</button>
                    <span style={{...S.cVal,...(adj!==0?{color:adj>0?B.wasabi:B.sunshine,opacity:1}:{})}}>{rolls}</span>
                    <button style={S.cBtn} onClick={()=>adjSku(s.name,1)}>+</button>
                  </div>
                  <div style={S.skuPortionsVal}>{portions}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Confirm — disabled when already confirmed locally OR by someone else on a different device */}
        <button style={{...S.submitBtn,...(isConfirmed?{opacity:0.6}:{}),marginBottom:10}} onClick={handleConfirm} disabled={isConfirmed||confirming}>
          {confirming
            ? "Confirming…"
            : confirmed
              ? "✓ Forecast Confirmed"
              : isRemotelyConfirmed
                ? "✓ Already confirmed"
                : (confirmedInfo && confirmedInfo.undone
                    ? "Resubmit Forecast"
                    : "Confirm Forecast")}
        </button>
      </div>)}

      {/* Reason modal — shown when override ≠ 0 and staff taps Confirm */}
      {reasonModal && (
        <div style={S.overlay} onClick={()=>setReasonModal(false)}>
          <div style={S.modal} onClick={e=>e.stopPropagation()}>
            <div style={S.modalTitle}>Override Active</div>
            <div style={{fontSize:"0.88rem",color:B.kewpie,opacity:0.8,marginBottom:12,lineHeight:1.5}}>Forecast adjusted by <strong style={{color:B.wasabi}}>{override>0?"+":""}{override}%</strong>. Please provide a reason.</div>
            <textarea rows="3" style={{...S.modalInput,minHeight:72,resize:"vertical",letterSpacing:"normal",textAlign:"left",fontSize:"0.95rem",borderColor:reasonErr?B.coral:undefined}} placeholder="e.g. Event nearby, gut feel, school holiday week…" value={reasonText} onChange={e=>{setReasonText(e.target.value);if(reasonErr)setReasonErr(false)}}/>
            <div style={S.modalBtns}>
              <button style={S.modalCancel} onClick={()=>setReasonModal(false)}>Cancel</button>
              <button style={S.modalOk} onClick={submitReason}>Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { MeadowhallForecast };
